import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, AlertCircle, Send, ArrowRight } from "lucide-react";
import { LOGO_URL } from "../components/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

function Field({ q, value, onChange }) {
  const setVal = (v) => onChange(q.id, v);

  if (q.type === "textarea") {
    return (
      <textarea
        rows={4}
        required={q.required}
        className="w-full p-3 bg-transparent border outline-none focus:border-[var(--gai-red)] resize-none text-[var(--gai-black)]"
        style={{ borderColor: "rgba(5,16,36,0.25)" }}
        value={value || ""}
        onChange={(e) => setVal(e.target.value)}
        data-testid={`q-${q.id}`}
      />
    );
  }
  if (q.type === "select" || q.type === "radio") {
    return (
      <select
        required={q.required}
        className="w-full p-3 bg-transparent border outline-none focus:border-[var(--gai-red)] text-[var(--gai-black)]"
        style={{ borderColor: "rgba(5,16,36,0.25)" }}
        value={value || ""}
        onChange={(e) => setVal(e.target.value)}
        data-testid={`q-${q.id}`}
      >
        <option value="">— Choisir —</option>
        {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (q.type === "multiselect") {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (o) => {
      if (selected.includes(o)) setVal(selected.filter((x) => x !== o));
      else setVal([...selected, o]);
    };
    return (
      <div className="flex flex-wrap gap-2" data-testid={`q-${q.id}`}>
        {(q.options || []).map((o) => {
          const on = selected.includes(o);
          return (
            <button
              type="button"
              key={o}
              onClick={() => toggle(o)}
              className={`px-3 py-2 text-xs tracking-wide border transition ${
                on
                  ? "bg-[var(--gai-white)] text-[var(--gai-black)] border-[var(--gai-black)]"
                  : "border-[rgba(5,16,36,0.3)] text-[var(--gai-black)] hover:border-[var(--gai-red)]"
              }`}
            >
              {on ? "✓ " : ""}{o}
            </button>
          );
        })}
      </div>
    );
  }
  const inputType = q.type === "email" ? "email" : q.type === "phone" ? "tel" : q.type === "number" ? "number" : "text";
  return (
    <input
      type={inputType}
      required={q.required}
      className="w-full p-3 bg-transparent border outline-none focus:border-[var(--gai-red)] text-[var(--gai-black)]"
      style={{ borderColor: "rgba(5,16,36,0.25)" }}
      value={value || ""}
      onChange={(e) => setVal(e.target.value)}
      data-testid={`q-${q.id}`}
    />
  );
}

export default function QuestionnairePublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/questionnaire/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data?.invite?.status === "completed" && r.data?.invite?.answers) {
          setAnswers(r.data.invite.answers);
          setDone(true);
        }
      })
      .catch((e) => setError(e?.response?.data?.detail || "Lien invalide ou expiré."));
  }, [token]);

  const onChange = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Custom validation for required multiselect/radio fields (browser 'required' doesn't apply to chip groups)
    const missing = (template.questions || []).find((q) => {
      if (!q.required) return false;
      const v = answers[q.id];
      if (q.type === "multiselect") return !Array.isArray(v) || v.length === 0;
      if (q.type === "radio") return !v;
      return false;
    });
    if (missing) {
      setValidationError(`Champ obligatoire : « ${missing.label} »`);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setValidationError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/questionnaire/${token}`, { answers });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setValidationError(err?.response?.data?.detail || "Erreur lors de l'envoi.");
    } finally { setSubmitting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--gai-white)" }} data-testid="questionnaire-error">
        <div className="max-w-md text-center">
          <AlertCircle size={36} className="mx-auto text-[var(--gai-black)] opacity-70" />
          <h2 className="serif text-3xl mt-4 text-[var(--gai-black)]">Lien indisponible</h2>
          <p className="text-sm text-[rgba(5,16,36,0.7)] mt-3">{error}</p>
          <Link to="/" className="gai-btn-gold mt-6 inline-flex">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--gai-white)" }}>
        <div className="text-[var(--gai-black)] text-sm">Chargement…</div>
      </div>
    );
  }

  const { template, invite } = data;

  return (
    <div className="min-h-screen" style={{ background: "var(--gai-white)" }} data-testid="page-questionnaire-public">
      {/* Header */}
      <header className="border-b" style={{ borderColor: "rgba(5,16,36,0.1)", background: "var(--gai-white)" }}>
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="GAI" className="w-9 h-9 object-contain" draggable="false" />
            <div>
              <div className="serif text-base text-[var(--gai-black)] leading-tight">GLOBAL AFRIQUE</div>
              <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[rgba(5,16,36,0.55)]">International</div>
            </div>
          </Link>
          <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[rgba(5,16,36,0.55)] hidden sm:block">
            Questionnaire client
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        {done ? (
          <div className="text-center" data-testid="questionnaire-success">
            <div className="w-16 h-16 mx-auto flex items-center justify-center" style={{ background: "var(--gai-black)", color: "var(--gai-red)" }}>
              <Check size={32} />
            </div>
            <div className="overline mt-6" style={{ color: "rgba(5,16,36,0.6)" }}>Réponses bien reçues</div>
            <h1 className="serif text-4xl md:text-5xl mt-3 text-[var(--gai-black)] leading-tight">
              Merci pour votre temps.
            </h1>
            <p className="mt-5 text-[rgba(5,16,36,0.75)] text-lg leading-relaxed max-w-xl mx-auto">
              Votre brief a été transmis à notre équipe éditoriale & commerciale. Nous vous recontactons sous 48 h ouvrées avec une proposition adaptée.
            </p>
            <Link to="/" className="gai-btn-gold mt-10 inline-flex">
              Découvrir GAI <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <>
            <div className="overline" style={{ color: "rgba(5,16,36,0.6)" }}>
              {invite.client_company || invite.client_name || "Pour vous"}
            </div>
            <h1 className="serif text-4xl md:text-5xl mt-3 text-[var(--gai-black)] leading-tight">
              {template.name}
            </h1>
            {template.description && (
              <p className="mt-4 text-[rgba(5,16,36,0.6)] text-sm">{template.description}</p>
            )}
            <div className="my-8 h-px" style={{ background: "rgba(5,16,36,0.15)" }} />

            {invite.custom_intro && (
              <div className="mb-8 p-5 border-l-2 italic text-[rgba(5,16,36,0.85)] text-base leading-relaxed" style={{ borderColor: "var(--gai-red)", background: "var(--gai-grey-50)" }}>
                « {invite.custom_intro} »
              </div>
            )}
            {template.intro && (
              <p className="mb-10 text-[rgba(5,16,36,0.78)] text-lg leading-relaxed">{template.intro}</p>
            )}

            <form onSubmit={submit} className="space-y-7" data-testid="questionnaire-form">
              {validationError && (
                <div className="p-4 border-l-2 text-sm" style={{ borderColor: "#b91c1c", background: "rgba(185,28,28,0.06)", color: "#7a1d1d" }} data-testid="questionnaire-validation-error">
                  {validationError}
                </div>
              )}
              {(template.questions || []).map((q, i) => (
                <div key={q.id}>
                  <label className="block text-[0.7rem] tracking-[0.2em] uppercase text-[rgba(5,16,36,0.7)] mb-2">
                    {String(i + 1).padStart(2, "0")} · {q.label}
                    {q.required && <span className="ml-1" style={{ color: "var(--gai-red)" }}>*</span>}
                  </label>
                  <Field q={q} value={answers[q.id]} onChange={onChange} />
                  {q.help && <div className="text-xs text-[rgba(5,16,36,0.5)] mt-1.5">{q.help}</div>}
                </div>
              ))}

              <div className="pt-6 flex justify-end">
                <button
                  disabled={submitting}
                  className="px-8 py-4 tracking-[0.18em] text-xs uppercase font-semibold transition hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-2"
                  style={{ background: "var(--gai-red)", color: "var(--gai-white)" }}
                  data-testid="questionnaire-submit"
                >
                  {submitting ? "Envoi…" : (<><Send size={13} /> Envoyer mes réponses</>)}
                </button>
              </div>
            </form>
          </>
        )}
      </main>

      <footer className="border-t mt-16 py-6" style={{ borderColor: "rgba(5,16,36,0.1)" }}>
        <div className="max-w-3xl mx-auto px-6 text-[0.65rem] tracking-[0.22em] uppercase text-[rgba(5,16,36,0.5)] text-center">
          GLOBAL AFRIQUE INTERNATIONAL · Vos réponses sont confidentielles.
        </div>
      </footer>
    </div>
  );
}
